import { useState, useEffect } from 'react'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder, faEllipsisV, faUsers, faLock, faPen, faFolderOpen } from '@fortawesome/free-solid-svg-icons'
import { useDroppable, useDraggable, useDndContext } from '@dnd-kit/core'
import { folderDragId, folderDropId } from 'dashboard/folders/folder-drag'
import { folderContentsLabel } from 'dashboard/folders/folder-contents-label'

const FolderCard = ({
  folder,
  onClick,
  onEdit,
  onDelete,
  onShare,
  onMove,
  itemCount,
  subfolderCount = 0,
  sharedUserCount = 0,
  isMoving = false,
  acceptsDrag
}) => {
  const [mouseOver, setMouseOver] = useState(false)
  const isOwner = folder.is_owner === true

  const { isOver, setNodeRef } = useDroppable({
    id: folderDropId(folder.id)
  })

  // Folders drag too, so a whole branch can be moved into another one. Only the owner may move it —
  // the API refuses a reparent from anyone else.
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging
  } = useDraggable({
    id: folderDragId(folder.id),
    disabled: !isOwner || isMoving,
    data: { type: 'folder', folderId: folder.id, isOwner }
  })

  const { active } = useDndContext()
  const activeData = active?.data?.current
  const isDraggingOwnedItem = activeData?.isOwner !== false
  const canDrop =
    (isOwner || folder.is_editor) &&
    isDraggingOwnedItem &&
    (!acceptsDrag || acceptsDrag(folder, activeData))
  const isOverShared = isOver && !canDrop
  const isOverOwned = isOver && canDrop

  useEffect(() => {
    if (isOverShared) {
      document.body.classList.add('cursor-not-allowed')
    } else {
      document.body.classList.remove('cursor-not-allowed')
    }
    return () => document.body.classList.remove('cursor-not-allowed')
  }, [isOverShared])

  // All three item types, Reports included: `reports` is `dependent: :nullify`, so a folder that
  // looks empty because Reports were left out of the count deletes without warning and silently
  // unfiles them. Not gated on the Reports beta — the records are there either way.
  const totalItems =
    (folder.dashboards_count || 0) + (folder.audit_reports_count || 0) + (folder.reports_count || 0)
  const blockingCount = totalItems + subfolderCount

  return (
    <Col md={4}>
      <div ref={setNodeRef}>
        <div
          ref={setDragRef}
          {...listeners}
          {...attributes}
          style={
            transform
              ? {
                  transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
                  opacity: isDragging ? 0.5 : 1,
                  zIndex: isDragging ? 1000 : undefined
                }
              : undefined
          }
        >
          <Card
            className="h-100 shadow-sm dashboard-card"
            style={{
              cursor: isOverShared ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s, border-color 0.2s',
              backgroundColor: isOverOwned ? '#e8f0fe' : isOverShared ? '#f8d7da' : mouseOver ? '#f8f9fa' : 'white',
              borderColor: isOverOwned ? '#4767C5' : isOverShared ? '#dc3545' : undefined,
              borderWidth: isOver ? '2px' : '1px',
              opacity: isOverShared ? 0.7 : isMoving ? 0.4 : 1
            }}
            onMouseEnter={() => setMouseOver(true)}
            onMouseLeave={() => setMouseOver(false)}
            onClick={() => onClick(folder)}
          >
            <Card.Body>
              <Card.Title className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center flex-grow-1">
                  <FontAwesomeIcon
                    icon={subfolderCount > 0 ? faFolderOpen : faFolder}
                    className="me-2"
                    style={{ fontSize: '1.2rem', color: '#6c757d' }}
                  />
                  {folder.name}
                  {!isOwner && (
                    <span className="ms-2 badge bg-light text-muted" style={{ fontSize: '0.65rem', fontWeight: 'normal' }}>
                      <FontAwesomeIcon icon={folder.is_editor ? faPen : faLock} className="me-1" />
                      {folder.is_editor ? 'Editor' : 'View only'}
                    </span>
                  )}
                </div>
                <div
                  onClick={(e) => e.stopPropagation()}
                  // dnd-kit claims pointerdown on the draggable wrapper; without this the dropdown
                  // never opens on a folder card.
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="link"
                      className="text-muted p-0 border-0"
                      bsPrefix="btn"
                      data-testid={`folder-menu-${folder.id}`}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {isOwner && (
                        <Dropdown.Item onClick={() => onShare(folder)}>
                          Share
                        </Dropdown.Item>
                      )}
                      {isOwner && (
                        <Dropdown.Item onClick={() => onEdit(folder)}>
                          Rename
                        </Dropdown.Item>
                      )}
                      {isOwner && onMove && (
                        <Dropdown.Item onClick={() => onMove(folder)}>
                          Move to folder
                        </Dropdown.Item>
                      )}
                      {isOwner && (
                        <Dropdown.Item
                          onClick={() => onDelete(folder)}
                          className={blockingCount > 0 ? 'text-muted' : 'text-danger'}
                          disabled={blockingCount > 0}
                        >
                          Delete
                          {blockingCount > 0 && (
                            <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>
                              ({folderContentsLabel(subfolderCount, totalItems)})
                            </span>
                          )}
                        </Dropdown.Item>
                      )}
                      {!isOwner && (
                        <Dropdown.Item disabled className="text-muted">
                          No actions available
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Card.Title>
              <div className="mb-2">
                <small className="text-muted">
                  {subfolderCount > 0 && (
                    <>
                      {subfolderCount} {subfolderCount === 1 ? 'folder' : 'folders'}
                      {' '}&bull;{' '}
                    </>
                  )}
                  {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  {sharedUserCount > 0 && (
                    <>
                      {' '}&bull;{' '}
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      {sharedUserCount} {sharedUserCount === 1 ? 'user' : 'users'}
                    </>
                  )}
                </small>
              </div>
              {!isOwner && folder.owner && (
                <div>
                  <small className="text-muted fst-italic">
                    Shared by {folder.owner.first_name} {folder.owner.last_name}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </Col>
  )
}

export default FolderCard
